import { queryOptions } from '@tanstack/react-query';

import config from 'config';
import { createAPIError } from 'utils/exceptions/apiError';

export type CouponEligibility =
  | { status: 'date_range_valid'; couponCode: string; expiryDate: string }
  | { status: 'expired'; couponCode: string }
  | { status: 'not_found' };

export type DateRangeValidCoupon = Extract<
  CouponEligibility,
  { status: 'date_range_valid' }
>;

async function fetchCouponEligibility(
  couponCode: string,
): Promise<CouponEligibility> {
  const url = `${
    config.PARTNERS_SELF_SIGNUP_BFF_URL
  }/v1/coupon/${encodeURIComponent(couponCode)}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw await createAPIError(response);
  }

  return response.json();
}

export function getCouponEligibilityQueryOptions(
  couponCode: string | undefined,
) {
  return queryOptions({
    queryKey: ['coupon-eligibility', couponCode],
    queryFn: () =>
      couponCode ? fetchCouponEligibility(couponCode) : Promise.resolve(null),
    // Eligibility can flip while the page sits open; opt out of the client's
    // global 12h staleTime.
    staleTime: 0,
    // Awaited in route loaders; the default 3 retries would stall navigation.
    retry: 1,
  });
}

/** Expired is forwardable too (the BFF re-validates it); not_found never is. */
export function isForwardableCoupon(
  coupon: CouponEligibility | null | undefined,
): boolean {
  return coupon?.status === 'date_range_valid' || coupon?.status === 'expired';
}
